import React, { useState, useEffect } from "react";
import { db, auth } from "../firebase";
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  onSnapshot,
  orderBy,
} from "firebase/firestore"; 
import "../styles/user.css";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";
import "jspdf-autotable"; // ✅ This is required to add the plugin
import { updateDoc, doc, deleteDoc } from "firebase/firestore";

console.log(typeof jsPDF.prototype.autoTable); // should log 'function'

const UserDashboard = () => {
  const [hid, sethid] = useState("");
  const [name, setName] = useState("");
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [leaveReason, setLeaveReason] = useState("");
  const [leaveStatus, setLeaveStatus] = useState(null);
  const [comments, setComments] = useState([]); // 🔹 comments state
  const [userName, setUserName] = useState(""); // user's personal name
  const [popupImage, setPopupImage] = useState(null);

  const navigate = useNavigate();
  
  const [leaveHistory, setLeaveHistory] = useState([]);

  // 🔹 Fetch previous comments (read-only)
  useEffect(() => {
    const q = query(
      collection(db, "observations"),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments(list);
    });

    return () => unsubscribe();
  }, []);
    
  const exportPDF = () => { 
    const doc = new jsPDF();

    doc.setFontSize(16);
    doc.text("Leave History Report", 14, 20);

    if (employeeData) {
      doc.setFontSize(12);
      doc.text(`Name: ${employeeData.name}`, 14, 30);
      doc.text(`HID: ${employeeData.hid}`, 14, 38);
    }

    const tableColumn = ["Type", "Date", "Reason", "Status", "Requested On", "Admin Message"];
    const tableRows = [];

    // Iterate through leaveHistory and extract valid rows
    leaveHistory?.forEach((req) => {
      req.leaveDates?.forEach((d) => {
        // You can remove this "if" if you want to include all requests, not only non-pending
        if (d.status !== "Pending") {
          tableRows.push([
            d.leaveType || "-",
            d.date || "-",
            req.reason || "-",
            d.status || "-",
            new Date(req.date).toLocaleDateString(),
            d.note || req.message || "-"
          ]);
        }
      });
    });

    if (tableRows.length === 0) {
      alert("No approved/rejected leave records to export.");
      return;
    }

    doc.autoTable({
      startY: 45,
      head: [tableColumn],
      body: tableRows,
      styles: { fontSize: 10 },
    });

    doc.save("hotel_history.pdf");
  };

  useEffect(() => {
    const fetchUserName = async () => {
      try {
        const email = auth.currentUser?.email;
        if (!email) return;

        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", email));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const userDoc = snapshot.docs[0];
          const userData = userDoc.data();
          setUserName(userData.name); // this is the personal name
        } else {
          setUserName(""); // fallback
        }
      } catch (err) {
        console.error("Failed to fetch user name:", err);
      }
    };

    fetchUserName();
  }, []);

  useEffect(() => {
    const fetchEmployeeByEmail = async () => {
      setLoading(true);
      try {
        const userEmail = auth.currentUser?.email;
        if (!userEmail) {
          throw new Error("No user email found.");
        }

        const employeesRef = collection(db, "employees");
        const q = query(employeesRef, where("mail", "array-contains", userEmail));
        const snapshot = await getDocs(q);

        if (!snapshot.empty) {
          const employeeDoc = snapshot.docs[0];
          const employeeData = employeeDoc.data();
          setEmployeeData(employeeData);
          sethid(employeeData.hid); // Optional: if you're still using it

          // Load leave history
          const leaveQuery = query(
            collection(db, "leaveRequests"),
            where("hid", "==", employeeData.hid)
          );
          const leaveSnapshot = await getDocs(leaveQuery);
          const requests = leaveSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setLeaveHistory(requests);

          const sorted = requests.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
          const latest = sorted[0];

          if (latest) {
            const statuses = latest.leaveDates?.map(d => d.status) || [];

            if (statuses.length === 0) setLeaveStatus(null);
            else if (statuses.every(s => s === "Approved")) setLeaveStatus("Approved");
            else if (statuses.every(s => s === "Rejected")) setLeaveStatus("Rejected");
            else if (statuses.every(s => s === "Pending")) setLeaveStatus("Pending");
            else setLeaveStatus("Partially Approved");
          } else {
            setLeaveStatus(null);
          }

        } else {
          setEmployeeData(null);
          alert("No hotel record found for this email.");
        }
      } catch (error) {
        console.error("Error fetching employee by email:", error);
        alert("Something went wrong loading your hotel info.");
      }
      setLoading(false);
    };

    fetchEmployeeByEmail();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("Logout failed:", error);
      alert("Failed to log out.");
    }
  };

  function timeAgo(date) {
    if (!date) return "Just now";

    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);

    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  }

  return (
    <div className="user-dashboard">
      <button onClick={handleLogout} className="log-out"> Logout </button> 
       
      <div className="welcome-section">
        {/* <div className="flex justify-end mb-4"></div> */}

        {/* ✅ FIX: Add conditional rendering */}
        <h2 className="welcome-heading">
          Welcome To {employeeData ? employeeData.name : "Loading..."}
        </h2>

        {loading && <p style={{ color: "maroon" }}>Loading...</p>}

        {employeeData && (
          <div> 
            <h3>Your Details</h3>
            <h4><strong>Company:</strong> {employeeData.name}</h4>

            <h3><strong>Name :</strong> {userName || auth.currentUser?.email}</h3>

            <button
              className="login-btn"
              onClick={() => navigate("/scanqr", { state: { hotelName: employeeData.name } })}
            >
              Scan QR
            </button>
          </div>
        )}

        {/* 🔹 Display previous comments */}
        <div className="comment-section">
          <h3>OBSERVATIONS</h3>
          {comments.length > 0 ? (
            <ul className="comment-list">
              {comments
                .filter(c => c.status !== "Completed" && c.hotelName === employeeData?.name)
                .map(c => {
                  const date = c.createdAt?.toDate
                    ? c.createdAt.toDate().toLocaleString()
                    : "Just now";

                  return (
                    <li key={c.id} className="comment-item">
                      <p className="comment-email">👤 {c.userEmail?.split("@")[0]}</p>
                      <div className="comment-header">
                        <span className="comment-text">📝 {c.text || "(No text)"}</span>
                        <span className="comment-date">
                          📅 {c.createdAt?.toDate ? timeAgo(c.createdAt.toDate()) : "Just now"}                          
                        </span>
                        {c.images && c.images.length > 0 && (
                          <div className="comment-images">
                            {c.images.map((url, i) => (
                              <img
                                key={i}
                                src={url}
                                alt={`comment-${i}`}
                                className="comment-img"
                                onClick={() => setPopupImage(url)}
                                style={{ cursor: "pointer" }}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    </li>
                  );
                })}
            </ul>
          ) : (
            <p>No Observations yet.</p>
          )}
        </div>

        {/* 🔹 IMAGE POPUP */}
        {popupImage && (
          <div
            className="image-popup"
            onClick={() => setPopupImage(null)} // close on click
          >
            <img src={popupImage} alt="popup" className="popup-img" />
          </div>
        )}
      </div>
    </div>
  );
};

export default UserDashboard;